import { useEffect, useRef, useState } from "react";
import axios from "../../../../../utils/cmsAxios";

export const OBSERVATION_MS = 20 * 60 * 1000;
const EMPTY = { phase: "IDLE", jobId: null, status: "", message: "", checking: false, canRelease: false };
const ACTIVE = ["SUBMITTING", "CHECKING", "QUEUED", "RUNNING", "CHECK_REQUIRED", "UNKNOWN", "NEEDS_REVIEW"];

// This tracker only observes jobs. Expiry/disposal never cancels or resubmits a server job.
export function createManualUploadTracker({ userId, submit, getStatus, getActive, onChange, storage,
  notify = () => {}, observationMs = OBSERVATION_MS, pollMs = 2000 }) {
  const key = `cms:manual-upload:${userId}`;
  let state = { ...EMPTY };
  let disposed = false;
  let version = 0;
  let timer;
  let controller;
  let deadline = 0;
  const emit = (update) => {
    state = { ...state, ...update };
    if (!disposed) onChange(state);
  };
  const persist = (value) => {
    try { if (value) storage?.setItem(key, JSON.stringify(value)); else storage?.removeItem(key); } catch { /* Storage may be disabled. */ }
  };
  const stop = () => {
    version += 1;
    clearTimeout(timer);
    controller?.abort();
    return version;
  };
  const current = (requestVersion) => !disposed && requestVersion === version;
  const uncertainty = (message, phase = "CHECK_REQUIRED", canRelease = false) => {
    emit({ phase, checking: false, message, canRelease });
  };
  const consume = (job) => {
    if (!job?.job_id || String(job.user_id) !== String(userId)
      || (state.jobId && job.job_id !== state.jobId)) throw new Error("작업 응답을 확인할 수 없습니다.");
    const status = String(job.status || "").toUpperCase();
    if (!["QUEUED", "RUNNING", "SUCCESS", "FAILED", "NEEDS_REVIEW"].includes(status)) {
      throw new Error("알 수 없는 작업 상태입니다.");
    }
    persist({ jobId: job.job_id });
    const message = {
      QUEUED: "수동업로드가 대기 중입니다. 앞선 작업이 끝나면 시작됩니다.",
      RUNNING: "수동업로드가 진행 중입니다.",
      SUCCESS: "인스타 수동업로드가 완료되었습니다.",
      FAILED: job.error_message || "서버에서 수동업로드 실패를 확인했습니다.",
      NEEDS_REVIEW: `${job.error_message || "게시 여부를 확인할 수 없습니다."} 인스타 게시물과 히스토리를 확인하기 전에는 다시 업로드하지 마세요.`,
    }[status];
    const changed = state.phase !== status || state.jobId !== job.job_id;
    emit({ phase: status, status, jobId: job.job_id, message, checking: false, canRelease: status === "NEEDS_REVIEW" });
    if (["SUCCESS", "FAILED"].includes(status)) persist(null);
    if (changed && ["SUCCESS", "FAILED", "NEEDS_REVIEW"].includes(status)) {
      notify(`${status === "FAILED" ? "수동업로드 실패\n\n" : status === "NEEDS_REVIEW" ? "인스타 게시 여부 확인 필요\n\n" : ""}${message}`);
    }
    return ["QUEUED", "RUNNING"].includes(status);
  };
  const poll = async (requestVersion) => {
    if (!current(requestVersion)) return;
    if (Date.now() >= deadline) {
      const message = "20분간의 자동 상태 조회를 마쳤습니다. 업로드 실패나 취소가 아니며 서버 작업은 계속 대기·진행할 수 있습니다. 상태 재조회를 눌러 확인해 주세요.";
      uncertainty(message);
      notify(message);
      return;
    }
    controller = new AbortController();
    try {
      const job = await getStatus(state.jobId, controller.signal);
      if (!current(requestVersion)) return;
      if (consume(job)) timer = setTimeout(() => poll(requestVersion), pollMs);
    } catch (error) {
      if (!current(requestVersion)) return;
      uncertainty("작업 상태를 조회하지 못했습니다. 실제 업로드 실패가 아니며 서버 작업은 계속될 수 있습니다. 새 요청 대신 상태 재조회로 확인해 주세요.");
    }
  };
  const observe = () => {
    const requestVersion = stop();
    deadline = Date.now() + observationMs;
    emit({ checking: true });
    return poll(requestVersion);
  };
  const discover = async (unknown = false) => {
    const requestVersion = stop();
    controller = new AbortController();
    emit({ phase: "CHECKING", checking: true, canRelease: false });
    try {
      const job = await getActive(controller.signal);
      if (!current(requestVersion)) return;
      if (job !== null && (!job || typeof job !== "object")) throw new Error("작업 조회 응답을 확인할 수 없습니다.");
      if (!job) {
        if (unknown) uncertainty("접수 결과를 확인할 수 없습니다. 현재 대기·진행 중인 작업은 없지만 이미 완료됐을 수 있습니다. 히스토리와 인스타 게시물을 먼저 확인해 주세요.", "UNKNOWN", true);
        else emit({ ...EMPTY });
        return;
      }
      if (consume(job)) await observe();
    } catch (error) {
      if (!current(requestVersion)) return;
      uncertainty("대기·진행 작업을 조회하지 못했습니다. 상태 재조회로 확인하기 전에는 새 업로드를 요청하지 마세요.", "UNKNOWN");
    }
  };
  return {
    async restore() {
      let saved;
      try { saved = JSON.parse(storage?.getItem(key) || "null"); } catch { /* Ignore invalid local data. */ }
      if (saved?.jobId) {
        emit({ jobId: saved.jobId, phase: "CHECK_REQUIRED", message: "이전 수동업로드 상태를 확인 중입니다." });
        await observe();
      } else await discover(Boolean(saved?.unknown));
    },
    async start() {
      if (disposed || ACTIVE.includes(state.phase)) return null;
      const requestVersion = stop();
      controller = new AbortController();
      persist({ unknown: true });
      emit({ ...EMPTY, phase: "SUBMITTING", message: "수동업로드 접수 중입니다." });
      try {
        const data = await submit(controller.signal);
        if (!current(requestVersion)) return null;
        if (data?.success !== true || data?.queued !== true || !data?.job_id
          || (data.user_id != null && String(data.user_id) !== String(userId))) {
          uncertainty("수동업로드 접수 결과 또는 작업 번호를 확인하지 못했습니다. 상태 재조회로 접수 여부를 확인해 주세요.", "UNKNOWN");
          return null;
        }
        emit({ jobId: data.job_id, status: "QUEUED", phase: "QUEUED", message: data.message || "수동업로드 대기 중입니다." });
        persist({ jobId: data.job_id });
        // Observe asynchronously; submitting the same job again is never a retry strategy.
        void observe();
        return data;
      } catch (error) {
        if (!current(requestVersion)) return null;
        if ([400, 401, 403, 404, 409, 422].includes(error?.response?.status)) {
          persist(null);
          emit({ ...EMPTY, phase: "REJECTED", message: error.response?.data?.detail || "수동업로드 요청이 접수되지 않았습니다." });
        } else uncertainty("수동업로드 접수 응답을 받지 못했습니다. 서버에서 접수됐을 수 있으므로 상태 재조회로 먼저 확인해 주세요.", "UNKNOWN");
        return null;
      }
    },
    async recheck() {
      if (disposed || state.checking || state.phase === "SUBMITTING") return;
      if (state.jobId) await observe(); else await discover(true);
    },
    releaseAfterReview() {
      if (!state.canRelease || state.checking) return;
      stop();
      persist(null);
      emit({ ...EMPTY });
    },
    dispose() { disposed = true; stop(); },
  };
}

export default function useManualUpload(userId, enabled) {
  const [tracked, setTracked] = useState({ userId, ...EMPTY });
  const tracker = useRef(null);
  useEffect(() => {
    if (!enabled || !userId) return undefined;
    const base = process.env.REACT_APP_FASTAPI_ADS_URL;
    let storage;
    try { storage = window.sessionStorage; } catch { /* Tracking still works in memory. */ }
    const current = createManualUploadTracker({
      userId, storage,
      onChange: (next) => setTracked({ userId, ...next }),
      notify: (message) => window.alert(message),
      submit: async (signal) => (await axios.post(`${base}/auto-upload/manual-run/${userId}`, null, { signal, timeout: 15000 })).data,
      getStatus: async (jobId, signal) => (await axios.get(`${base}/auto-upload/manual-run/status/${encodeURIComponent(jobId)}`, { signal, timeout: 15000 })).data,
      getActive: async (signal) => (await axios.get(`${base}/auto-upload/manual-run/active/${userId}`, { signal, timeout: 15000 })).data.job,
    });
    tracker.current = current;
    void current.restore();
    return () => { current.dispose(); tracker.current = null; };
  }, [userId, enabled]);
  const state = enabled && tracked.userId === userId ? tracked : EMPTY;
  return { ...state, blocked: ACTIVE.includes(state.phase),
    start: () => tracker.current?.start(), recheck: () => tracker.current?.recheck(),
    releaseAfterReview: () => tracker.current?.releaseAfterReview() };
}
